import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalFooter,
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  Button,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  IconButton,
  Tooltip,
  Badge,
  Select,
  Avatar,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PencilSimple,
  Trash,
  Lock,
  PaperPlaneTilt,
  CalendarBlank,
  ChatCircle,
  Tag,
  TextAlignLeft,
  Check,
  ClockCounterClockwise,
  Paperclip,
  DownloadSimple,
  File as FileIcon,
  Image,
  FilePdf,
  PenNib,
  CheckCircle,
  XCircle,
  FileDoc,
  Copy,
  ArrowSquareOut,
  MagnifyingGlass,
} from 'phosphor-react';
import moment from 'moment';
import {
  IKanbanCard,
  IKanbanCampoDefinicao,
  IKanbanCampoValorInput,
  IKanbanAtividade,
  IKanbanAnexo,
  IKanbanUserOption,
  IKanbanUsuario,
  IKanbanVinculo,
  KanbanPrioridade,
  IKanbanComment,
  IKanbanResponse,
  useKanban,
} from '../../hooks/useKanban';
import { UserPapelPicker } from './UserPapelPicker';
import {
  IAutentiqueDocumentoEventos,
  IAutentiqueSignatarioEventos,
  useAutentique,
} from '../../hooks/useAutentique';
import { CorrigirAssinanteModal } from './CorrigirAssinanteModal';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';
import { MarkdownRenderer } from './MarkdownRenderer';
import { MentionTextarea, MentionTextareaRef, extractRawValue } from './MentionTextarea';
import { KanbanCustomFieldInput } from './KanbanCustomFieldInput';
import { KanbanDatePicker } from './KanbanDatePicker';
import { useAuth } from '../../providers/auth';
import { api } from '../../services/api';
import { semAcento } from '../../constants/ufs';
import { extractApiErrorMessage } from '../../utils/ErrorUtils';
import {
  TIPO_CONTRATO_ADITIVO,
  TIPO_CONTRATO_PADRAO,
  estadoDoTipo,
  rotuloDoTipo,
} from '../../pages/eventos/components/contrato/tiposDeContrato';
import SelectTipoContrato from '../../pages/eventos/components/contrato/SelectTipoContrato';
import {
  IRelatorioAssinaturas,
  RELATORIO_PADRAO,
  SecaoRelatorio,
  relatorioDoContrato,
  relatorioParaApi,
} from '../../pages/eventos/components/contrato/RelatorioAssinaturas';

// Converte os usuários vinculados ao card (que vêm como {id, nome, papel?}) p/
// o shape do seletor (IKanbanUserOption). email não vem no card → string vazia.
const usuariosToOptions = (us?: IKanbanUsuario[]): IKanbanUserOption[] =>
  (us ?? []).map(u => ({ id: u.id, nome: u.nome ?? '', email: '' }));

// O evento mais novo decide: e-mail devolvido e depois entregue não é problema.
const emailNaoEntregue = (sig: IAutentiqueSignatarioEventos) =>
  sig.status === 'pendente' && sig.eventos[sig.eventos.length - 1]?.tipo === 'email_recusado';

// Quem precisa de ação vem primeiro: em lista grande, ficava perdido no meio.
const naOrdemDeAcao = (signatarios: IAutentiqueSignatarioEventos[], aberto: boolean) =>
  aberto
    ? [...signatarios].sort(
        (a, b) =>
          Number(b.status === 'rejeitado' || emailNaoEntregue(b)) -
          Number(a.status === 'rejeitado' || emailNaoEntregue(a)),
      )
    : signatarios;

interface KanbanCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: IKanbanCard | null;
  campos?: IKanbanCampoDefinicao[];
  departamentoIds?: number[];
  onSave: (
    // eslint-disable-next-line no-unused-vars
    id: number,
    // eslint-disable-next-line no-unused-vars
    titulo: string,
    // eslint-disable-next-line no-unused-vars
    descricao: string,
    // eslint-disable-next-line no-unused-vars
    prioridade: KanbanPrioridade,
    // eslint-disable-next-line no-unused-vars
    expiraEm?: string | null,
    // eslint-disable-next-line no-unused-vars
    campoValores?: IKanbanCampoValorInput[],
    // eslint-disable-next-line no-unused-vars
    userIds?: number[],
    // Vínculos com papel (responsavel|supervisor); userIds segue p/ compat.
    // eslint-disable-next-line no-unused-vars
    vinculos?: IKanbanVinculo[],
  ) => void;
  // eslint-disable-next-line no-unused-vars
  onArchive: (id: number) => void;
  columnTitle?: string | null;
  isEventProject?: boolean;
  // Espelho de módulo: só-leitura — abre p/ VISUALIZAR, sem editar/salvar/mover/arquivar/
  // comentar/anexar/contrato. A edição real é na tela do módulo de origem.
  isReadOnly?: boolean;
  // Solução provisória (hotfix — ver TODO(RBAC) equivalente no backend,
  // app/api/v1/endpoints/kanban.py): quando true, QUALQUER usuário com acesso ao board
  // pode editar/arquivar o card (não só admin/diretoria/dev ou o responsável vinculado).
  // Usado onde o backend já libera a "equipe interna" (ex.: Kanban de Eventos dentro de
  // /eventos). Sem isto, `isCreator` continuaria travando a UI mesmo com o backend
  // aceitando a chamada. Substituir por permissão configurável (grupo/usuário) depois.
  allowAnyEditor?: boolean;
  // ── Pontos de extensão p/ domínios específicos (ex.: Estatutos) usarem o modal genérico
  //    em vez de um modal próprio (SOLID/Open-Closed: o genérico não conhece o domínio). ──
  // Ações do domínio, renderizadas na SIDEBAR direita (abaixo de Responsáveis) — ex.: os
  // botões do Estatuto (validar, Autentique, PDF, arquivar…). Default: nada.
  sidebarSlot?: React.ReactNode;
  // Aba extra (ao lado de Comentários/Anexos/Atividade/Assinatura) p/ o conteúdo rico do
  // domínio — ex.: a aba "Estatuto" (link público, datas, cartório, 22 campos do cliente).
  extraTab?: { key: string; label: string; icon?: React.ReactNode; content: React.ReactNode };
  // Esconde o botão "Arquivar card" genérico da sidebar. Usado por domínios que já expõem o
  // próprio arquivar no `sidebarSlot` (ex.: Estatutos) — evita dois botões de arquivar iguais.
  hideArchive?: boolean;
  // ── Mais pontos de extensão (legalização, 29/09) — todos desligados por padrão. ──
  // Esconde o lápis de edição (e o selo "Leitura"): o domínio edita pelos próprios meios.
  hideEdit?: boolean;
  // Nó ao lado do título, no lugar do lápis — a edição do domínio (ex.: renomear com aviso).
  tituloAcao?: React.ReactNode;
  // Conteúdo fixo entre o título e as abas (ex.: identificadores e trilha de etapas do processo).
  cabecalho?: React.ReactNode;
  // Blocos que o domínio não usa ou governa de outro jeito (ex.: na legalização o prazo muda
  // só com motivo e histórico, o responsável é do processo e não há prioridade nem Autentique).
  hideDescription?: boolean;
  hidePriority?: boolean;
  hideExpiracao?: boolean;
  hideResponsaveis?: boolean;
  hideAssinatura?: boolean;
  // Conteúdo próprio da aba Anexos — os mesmos anexos do card, com o que o domínio precisa a mais.
  anexosContent?: React.ReactNode;
  // Registro anterior aos comentários (ex.: a observação antiga): fica na ponta dos mais
  // antigos — no topo da lista, ou no fim com `comentariosDoMaisNovo`.
  comentariosAnteriores?: React.ReactNode;
  // Lista do mais novo para o mais antigo, com a caixa de escrever no topo. Desligado: do
  // mais antigo para o mais novo, a ordem da API.
  comentariosDoMaisNovo?: boolean;
  // Entrega ao domínio a função que envia o comentário digitado e ainda não enviado (`null`
  // ao sair), para enviá-lo junto das próprias ações (ex.: salvar a etapa) em vez de perder
  // o texto. Passe uma função estável (useCallback).
  // eslint-disable-next-line no-unused-vars
  onComentarioPendente?: (enviar: (() => Promise<void>) | null) => void;
  size?: string;
  // Altura fixa (ex.: '90vh'). Sem ela o modal centralizado muda de tamanho, e de lugar, a
  // cada troca de aba quando uma aba é bem mais alta que as outras.
  altura?: string;
}

// A descrição da atividade continua a frase depois do nome de quem fez: só a 1ª letra de
// palavra comum desce ("Moveu" → "moveu"); sigla no começo fica como está ("NF 123").
export const continuarFrase = (descricao: string): string =>
  /^[A-ZÀ-ÖØ-Þ][a-zß-öø-ÿ]/.test(descricao)
    ? descricao.charAt(0).toLowerCase() + descricao.slice(1)
    : descricao;

export const KanbanCardModal: React.FC<KanbanCardModalProps> = ({
  isOpen,
  onClose,
  card,
  campos = [],
  departamentoIds,
  onSave,
  onArchive,
  columnTitle,
  isEventProject,
  isReadOnly = false,
  allowAnyEditor = false,
  sidebarSlot,
  extraTab,
  hideArchive = false,
  hideEdit = false,
  tituloAcao,
  cabecalho,
  hideDescription = false,
  hidePriority = false,
  hideExpiracao = false,
  hideResponsaveis = false,
  hideAssinatura = false,
  anexosContent,
  comentariosAnteriores,
  comentariosDoMaisNovo = false,
  onComentarioPendente,
  size = '4xl',
  altura,
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const {
    getComments,
    createComment,
    getAtividades,
    getAnexos,
    uploadAnexo,
    deleteAnexo,
    getUsuariosMencao,
  } = useKanban();
  const queryClient = useQueryClient();
  // Aceita as abas fixas + a key da aba extra do domínio (extraTab.key), quando houver.
  const [activeTab, setActiveTab] = useState<
    'comentarios' | 'anexos' | 'atividade' | 'assinatura' | string
  >('comentarios');
  const {
    getEventos: getAutentiqueEventos,
    cancelarEnvio,
    reenviarPendentes,
    baixarRelatorioPdf,
  } = useAutentique();
  // Documento pendente cuja confirmação de "Cancelar envio" está aberta.
  const [envioParaCancelar, setEnvioParaCancelar] = useState<IAutentiqueDocumentoEventos | null>(
    null,
  );
  const [assinanteParaCorrigir, setAssinanteParaCorrigir] =
    useState<IAutentiqueSignatarioEventos | null>(null);
  const [buscaAssinante, setBuscaAssinante] = useState('');
  useEffect(() => setBuscaAssinante(''), [card?.id, isOpen]);
  // Link de assinatura copiado há pouco: o ícone vira "check" por 2 s.
  const [linkCopiado, setLinkCopiado] = useState<string | null>(null);
  const copiarLink = async (link?: string | null) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopiado(link);
      setTimeout(() => setLinkCopiado(atual => (atual === link ? null : atual)), 2000);
    } catch {
      toast({ title: 'Não foi possível copiar. Copie o link manualmente.', status: 'error' });
    }
  };
  // Responsáveis e supervisores (com papel) — Task #496.
  const [selectedUsers, setSelectedUsers] = useState<IKanbanUserOption[]>([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState<IKanbanUserOption[]>([]);

  const [isEditing, setIsEditing] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState<KanbanPrioridade>('media');
  const [expiraEm, setExpiraEm] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [mentionSearch, setMentionSearch] = useState<string | null>(null);
  const [mentionUsers, setMentionUsers] = useState<
    { id: number; nome: string; email: string; imagem_url?: string }[]
  >([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const commentRef = useRef<MentionTextareaRef>(null);
  const [campoValues, setCampoValues] = useState<Record<number, any>>({});

  const grupoId = Number(user?.grupo_id);
  const isAdminKanban = [1, 3, 5].includes(grupoId); // Admin, Diretoria, Dev
  const isResponsavel = card && card.usuarios?.some(u => u.id === user?.id);
  // No espelho de módulo (isReadOnly) NINGUÉM edita — o card abre só p/ visualizar. Como
  // `isCreator` governa quase toda a edição (lápis, descrição, salvar, arquivar, papéis),
  // zerá-lo aqui desabilita tudo de uma vez. Os poucos controles fora de isCreator
  // (comentar, anexar, contrato/IA) são guardados com `!isReadOnly` individualmente.
  // `allowAnyEditor` (hotfix): quando true, qualquer usuário com o board editável pode
  // editar/arquivar — não só admin/diretoria/dev ou o responsável vinculado ao card.
  const isCreator = (isAdminKanban || isResponsavel || allowAnyEditor) && !isReadOnly;

  const {
    data: commentsData,
    isLoading: isLoadingComments,
    isError: falhouLeituraDosComentarios,
  } = useQuery<IKanbanResponse<IKanbanComment[]>>({
    queryKey: ['kanban-comments', card?.id],
    queryFn: () => getComments(card!.id),
    enabled: isOpen && !!card,
  });

  const comments = commentsData?.data || [];
  // A API devolve do mais antigo para o mais novo.
  const comentariosNaOrdem = comentariosDoMaisNovo ? [...comments].reverse() : comments;
  // Releitura que falhou com a lista já em mãos: segue mostrando a lista.
  const erroNosComentarios = falhouLeituraDosComentarios && !commentsData;

  const { data: atividadesData, isLoading: isLoadingAtividades } = useQuery<
    IKanbanResponse<IKanbanAtividade[]>
  >({
    queryKey: ['kanban-atividades', card?.id],
    queryFn: () => getAtividades(card!.id),
    enabled: isOpen && !!card && activeTab === 'atividade',
  });

  const atividades = atividadesData?.data || [];

  const { data: assinaturaData, isLoading: isLoadingAssinatura } = useQuery({
    queryKey: ['kanban-assinatura', card?.id],
    queryFn: () => getAutentiqueEventos(card!.id),
    enabled: isOpen && !!card && activeTab === 'assinatura',
    // Cada consulta vai na Autentique (60/min para a conta toda); o fim da assinatura chega pelo push.
    refetchInterval: 120_000,
  });

  const assinaturaDocs = assinaturaData?.data || [];
  // Ainda recebe assinatura: 'pending' e sem recusa (uma recusa encerra o documento, mesmo antes de o banco sair
  // de 'pending', salvo no envio que segue na recusa). Fora disso, quem faltou "não assinou" e o link não vale mais.
  const docAberto = (doc: (typeof assinaturaDocs)[number]) =>
    doc.status === 'pending' &&
    (doc.segue_na_recusa || !doc.signatarios.some(sig => sig.status === 'rejeitado'));

  // Cancelar envio: mesma regra de editar o card, só em contrato de evento e nunca pelo portal do cliente
  // (o backend confere de novo).
  const podeCancelarEnvio = isCreator && !!isEventProject && grupoId !== 7;
  // O backend cancela TODOS os envios pendentes do card: o aviso mostra quem já assinou em qualquer um deles.
  const pendentes = assinaturaDocs.filter(doc => doc.status === 'pending');
  const aReenviar = assinaturaDocs
    .filter(docAberto)
    .flatMap(doc => doc.signatarios)
    .filter(sig => sig.status === 'pendente').length;
  // Filtra o que a aba já carregou: buscar não chama a Autentique.
  const termoAssinante = semAcento(buscaAssinante);
  const temListaLonga = assinaturaDocs.some(doc => doc.signatarios.length > 10);
  const assinantesDaAba = (doc: (typeof assinaturaDocs)[number]) =>
    naOrdemDeAcao(doc.signatarios, docAberto(doc)).filter(sig =>
      semAcento(`${sig.nome} ${sig.email}`).includes(termoAssinante),
    );
  const recusaNoTopo = (doc: (typeof assinaturaDocs)[number]) =>
    !termoAssinante && doc.signatarios.some(sig => sig.status === 'rejeitado');
  const jaAssinaram = Array.from(
    new Set(
      pendentes.flatMap(doc =>
        (doc.signatarios ?? []).filter(sig => sig.status === 'assinado').map(sig => sig.nome),
      ),
    ),
  );
  // Relatório de assinaturas em PDF (o do e-mail ao supervisor). Quem pode ver: `podeBaixarRelatorio`, abaixo.
  const baixarRelatorioMutation = useMutation({
    mutationFn: () => baixarRelatorioPdf(card!.id),
    onError: () => toast({ title: 'Não foi possível baixar o relatório.', status: 'error' }),
  });
  const atualizarAssinatura = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban-assinatura', card?.id] });
    queryClient.invalidateQueries({ queryKey: ['kanban-atividades', card?.id] });
    queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
  };
  const reenviarMutation = useMutation({
    mutationFn: () => reenviarPendentes(card!.id),
    onSuccess: () =>
      toast({
        title: 'Convites reenviados. Quem recebeu há pouco não recebe de novo.',
        status: 'success',
      }),
    onError: (err: any) =>
      toast({
        title: extractApiErrorMessage(err, 'Não foi possível reenviar os convites.'),
        status: 'error',
      }),
    onSettled: atualizarAssinatura,
  });
  const cancelarEnvioMutation = useMutation({
    mutationFn: () => cancelarEnvio(card!.id),
    onSuccess: resposta => {
      toast({
        title:
          resposta?.data?.contrato_status === 'contrato'
            ? 'Envio cancelado. O contrato voltou para CONTRATO.'
            : 'Envio cancelado.',
        status: 'success',
      });
      setEnvioParaCancelar(null);
      queryClient.invalidateQueries({ queryKey: ['kanban-assinatura', card?.id] });
      queryClient.invalidateQueries({ queryKey: ['kanban-anexos', card?.id] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.detail || 'Não foi possível cancelar o envio.',
        status: 'error',
      });
      // Pode já ter sido assinado ou cancelado por outra pessoa: a aba mostra o estado atual.
      setEnvioParaCancelar(null);
      queryClient.invalidateQueries({ queryKey: ['kanban-assinatura', card?.id] });
    },
  });

  // Invalidar board quando detectar rejeição ou assinatura completa (sync backend)
  useEffect(() => {
    if (!assinaturaDocs.length) return;
    const hasRejected = assinaturaDocs.some(doc =>
      doc.signatarios?.some((sig: any) => sig.status === 'rejeitado'),
    );
    const hasSigned = assinaturaDocs.some(doc => doc.status === 'signed');
    if (hasRejected || hasSigned) {
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
    }
  }, [assinaturaDocs, queryClient]);

  const { data: anexosData, isLoading: isLoadingAnexos } = useQuery<
    IKanbanResponse<IKanbanAnexo[]>
  >({
    queryKey: ['kanban-anexos', card?.id],
    queryFn: () => getAnexos(card!.id),
    enabled: isOpen && !!card,
  });
  const anexos = anexosData?.data || [];

  // A busca de usuários (responsáveis/supervisores) agora vive no UserPapelPicker.

  // ── Contrato (dados do card de evento) ──
  // No espelho read-only não mostra a UI de gerar/editar contrato (é mutação).
  const showContratoIA = isEventProject && columnTitle === 'CONTRATO' && !isReadOnly;
  const navigate = useNavigate();

  const { data: contratoCardData } = useQuery<any>({
    queryKey: ['card-contrato', card?.id],
    queryFn: () => api.get(`/api/v1/eventos/contrato-por-card/${card!.id}`).then(r => r.data),
    enabled: isOpen && !!card && !!isEventProject,
  });
  const contratoInfo = contratoCardData?.data;
  // Equipe interna, contrato de evento em assinatura. Card do quadro de eventos sem contrato também vai para
  // assinatura, mas sem contrato não há relatório (o back responde 404).
  const podeBaixarRelatorio =
    !!isEventProject && !!contratoInfo && grupoId !== 7 && pendentes.length > 0;
  // Sem contrato de evento o back recusa corrigir e reenviar: botão que só daria erro não aparece.
  const podeManterEnvio = podeCancelarEnvio && !!contratoInfo;

  // ── Campos editaveis do contrato ──
  const [contratoCategoria, setContratoCategoria] = useState<number | null>(null);
  const [contratoServicos, setContratoServicos] = useState<number[]>([]);
  const [contratoValor, setContratoValor] = useState('');
  const [contratoDtVencimento, setContratoDtVencimento] = useState('');
  const [contratoCnpj, setContratoCnpj] = useState('');
  // Qual modelo do evento gera o contrato (serviço, produto, parceria...).
  const [contratoTipo, setContratoTipo] = useState(TIPO_CONTRATO_PADRAO);
  const [relatorio, setRelatorio] = useState<IRelatorioAssinaturas>(RELATORIO_PADRAO);
  // O back só manda o relatório à equipe (o portal do cliente não vê nem configura).
  const mostraRelatorio = !isReadOnly && contratoInfo?.relatorio_emails !== undefined;
  // Escolhido no select e ainda não salvo: o "Gerar contrato" usaria o tipo salvo e a troca se perderia.
  const tipoNaoSalvo =
    !!contratoInfo && contratoTipo !== (contratoInfo.contrato_tipo ?? TIPO_CONTRATO_PADRAO);
  // Termo aditivo: quem gera e envia é o robô de eventos, não o "Gerar contrato" da tela.
  const termoAditivo = contratoInfo?.contrato_tipo === TIPO_CONTRATO_ADITIVO;
  // O estado do template do tipo escolhido, em texto: na barra lateral o select corta o sufixo da opção.
  const estadoDoTipoEscolhido = estadoDoTipo(
    (contratoInfo?.tipos_contrato ?? []).find((t: { valor: string }) => t.valor === contratoTipo),
  );

  const contratoSalvo = contratoInfo
    ? JSON.stringify([
        contratoInfo.contrato_id,
        contratoInfo.categoria_id,
        contratoInfo.servico_ids,
        contratoInfo.valor,
        contratoInfo.dt_vencimento,
        contratoInfo.fornecedor_documento,
        contratoInfo.contrato_tipo,
      ])
    : '';
  useEffect(() => {
    if (contratoInfo) {
      setContratoCategoria(contratoInfo.categoria_id ?? null);
      setContratoServicos(contratoInfo.servico_ids ?? []);
      setContratoValor(contratoInfo.valor ? String(contratoInfo.valor) : '');
      setContratoDtVencimento(contratoInfo.dt_vencimento ?? '');
      setContratoCnpj(contratoInfo.fornecedor_documento ?? '');
      setContratoTipo(contratoInfo.contrato_tipo ?? TIPO_CONTRATO_PADRAO);
    }
    // So' quando os campos do contrato mudam no servidor: o "Salvar relatório" refaz a busca e, com `[contratoInfo]`,
    // apagava o CNPJ/valor/categoria editados e ainda não salvos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoSalvo]);

  // Volta ao salvo ao ABRIR (o modal fica montado: uma edição não salva reapareceria como salva) e quando o
  // relatório salvo muda no servidor (o cache da abertura pode estar velho). Não a cada refetch: o "Salvar
  // contrato" refaz a busca e apagaria o relatório em edição.
  const relatorioSalvo = JSON.stringify(relatorioParaApi(relatorioDoContrato(contratoInfo)));
  useEffect(() => {
    if (isOpen) setRelatorio(relatorioDoContrato(contratoInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoInfo?.contrato_id, isOpen, relatorioSalvo]);
  const relatorioMudou =
    !!contratoInfo && JSON.stringify(relatorioParaApi(relatorio)) !== relatorioSalvo;

  // O relatório tem o próprio salvar: o "Salvar contrato" é só dos campos do contrato.
  const salvarRelatorioMutation = useMutation({
    mutationFn: (valor: IRelatorioAssinaturas) => {
      if (!contratoInfo) return Promise.reject(new Error('Contrato nao encontrado'));
      return api
        .put(
          `/api/v1/eventos/${contratoInfo.evento_id}/contratos/${contratoInfo.contrato_id}`,
          relatorioParaApi(valor),
        )
        .then(r => r.data);
    },
    onSuccess: () => {
      toast({ title: 'Relatório salvo!', status: 'success', duration: 3000 });
      queryClient.invalidateQueries({ queryKey: ['card-contrato', card?.id] });
    },
    onError: (err: any) => {
      const detalhe = err?.response?.data?.detail;
      toast({
        title: 'Erro ao salvar o relatório',
        description: typeof detalhe === 'string' ? detalhe : 'Confira os campos e tente novamente.',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Ao ABRIR o modal, se há aba de domínio (ex.: "Estatuto"), abre nela — é o conteúdo
  // principal p/ esses módulos. Sem extraTab, mantém o default "comentarios". Só roda na
  // transição de abertura (dep isOpen) p/ não brigar com a troca de aba pelo usuário.
  useEffect(() => {
    if (isOpen) {
      setActiveTab(extraTab ? extraTab.key : 'comentarios');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, extraTab?.key]);

  const servicosFiltrados = (contratoInfo?.servicos ?? []).filter(
    (s: any) => !contratoCategoria || s.categoria_id === contratoCategoria,
  );

  const updateContratoMutation = useMutation({
    mutationFn: (payload: any) => {
      if (!contratoInfo) return Promise.reject(new Error('Contrato nao encontrado'));
      return api
        .put(
          `/api/v1/eventos/${contratoInfo.evento_id}/contratos/${contratoInfo.contrato_id}`,
          payload,
        )
        .then(r => r.data);
    },
    onSuccess: () => {
      toast({
        title: 'Contrato atualizado!',
        status: 'success',
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ['card-contrato', card?.id] });
      // O tipo muda o catálogo e o template da ficha: a tela do contrato não pode abrir do cache.
      if (contratoInfo)
        queryClient.invalidateQueries({
          queryKey: ['evento-contrato-ficha', contratoInfo.evento_id],
        });
    },
    onError: (err: any) => {
      const detalhe = err?.response?.data?.detail;
      toast({
        title: 'Erro ao atualizar contrato',
        // O 400 de validação traz uma lista de objetos: como texto do aviso, derrubava a tela.
        description: typeof detalhe === 'string' ? detalhe : 'Confira os campos e tente novamente.',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleSaveContrato = () => {
    if (!contratoInfo) return;
    const valorNum = parseFloat(contratoValor.replace(/\./g, '').replace(',', '.'));
    updateContratoMutation.mutate({
      categoria_id: contratoCategoria || null,
      servico_ids: contratoServicos,
      valor: Number.isNaN(valorNum) ? 0 : valorNum,
      dt_vencimento: contratoDtVencimento || null,
      fornecedor_documento: contratoCnpj.replace(/\D/g, '') || null,
      // Só quando mudou: um tipo que saiu do catálogo daria erro em todo salvamento do card.
      ...(tipoNaoSalvo ? { contrato_tipo: contratoTipo } : {}),
    });
  };

  // Envio em curso: nem Enter de novo nem o registro do pendente mandam o mesmo texto 2 vezes.
  const enviandoComentarioRef = useRef(false);
  const createCommentMutation = useMutation({
    mutationFn: (commentText: string) => createComment(card!.id, { descricao: commentText }),
    onMutate: () => {
      enviandoComentarioRef.current = true;
    },
    onSettled: () => {
      enviandoComentarioRef.current = false;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-comments', card?.id] });
      setNewComment('');
    },
    onError: () => {
      toast({
        title: 'Erro ao enviar comentario',
        description: 'Tente novamente.',
        status: 'error',
        duration: 4000,
      });
    },
  });

  const uploadAnexoMutation = useMutation({
    mutationFn: (file: globalThis.File) => uploadAnexo(card!.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-anexos', card?.id] });
      queryClient.invalidateQueries({ queryKey: ['kanban-atividades', card?.id] });
      toast({ title: 'Arquivo enviado!', status: 'success', duration: 2000 });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao fazer upload',
        description: err?.response?.data?.detail || 'Tente novamente.',
        status: 'error',
        duration: 4000,
      });
    },
  });

  const deleteAnexoMutation = useMutation({
    mutationFn: (anexoId: number) => deleteAnexo(anexoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-anexos', card?.id] });
      queryClient.invalidateQueries({ queryKey: ['kanban-atividades', card?.id] });
      toast({ title: 'Anexo removido', status: 'info', duration: 2000 });
    },
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    Array.from(files).reduce<Promise<void>>(
      (chain, file) =>
        chain.then(() =>
          uploadAnexoMutation
            .mutateAsync(file)
            .then(() => {})
            .catch(() => {}),
        ),
      Promise.resolve(),
    );
    e.target.value = '';
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType: string | null) => {
    if (!contentType) return FileIcon;
    if (contentType.startsWith('image/')) return Image;
    if (contentType === 'application/pdf') return FilePdf;
    return FileIcon;
  };

  const handleAddComment = () => {
    if (newComment.trim() && card && !enviandoComentarioRef.current) {
      createCommentMutation.mutate(newComment.trim());
      setMentionSearch(null);
    }
  };

  // Lógica de @menções no textarea
  const handleCommentChange = useCallback(
    async (rawValue: string) => {
      setNewComment(rawValue);

      // Extrair texto visivel (sem markup de mencao) ate o cursor
      // Para detectar @busca, usamos o texto visivel do editor
      const el = commentRef.current?.getElement();
      if (!el) return;

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      // Pegar texto antes do cursor no node atual
      const range = sel.getRangeAt(0);
      const preRange = document.createRange();
      preRange.selectNodeContents(el);
      preRange.setEnd(range.startContainer, range.startOffset);
      const textBeforeCursor = preRange.toString();

      const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

      if (mentionMatch) {
        const search = mentionMatch[1];
        setMentionSearch(search);
        setMentionIndex(0);
        try {
          const res = await getUsuariosMencao(search);
          setMentionUsers(res.data || []);
        } catch {
          setMentionUsers([]);
        }
      } else {
        setMentionSearch(null);
        setMentionUsers([]);
      }
    },
    [getUsuariosMencao],
  );

  const insertMention = useCallback((mentionUser: { id: number; nome: string }) => {
    const editorEl = commentRef.current?.getElement();
    if (!editorEl) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);

    // Encontrar o @ no text node atual e remover o @busca
    const { startContainer, startOffset } = range;
    if (startContainer.nodeType === Node.TEXT_NODE) {
      const text = startContainer.textContent || '';
      const textBefore = text.slice(0, startOffset);
      const atIndex = textBefore.lastIndexOf('@');
      if (atIndex === -1) return;

      const textNode = startContainer as Text;
      const after = text.slice(startOffset);
      const before = text.slice(0, atIndex);

      // Criar o span de mencao
      const mentionSpan = document.createElement('span');
      mentionSpan.contentEditable = 'false';
      mentionSpan.setAttribute('data-mention-id', String(mentionUser.id));
      mentionSpan.setAttribute('data-mention-name', mentionUser.nome);
      Object.assign(mentionSpan.style, {
        color: '#2B6CB0',
        fontWeight: '600',
        background: '#EBF8FF',
        borderRadius: '4px',
        padding: '0 2px',
        userSelect: 'all',
      });
      mentionSpan.textContent = `@${mentionUser.nome}`;

      // Reconstruir: texto antes + span + espaco + texto depois
      textNode.textContent = before;
      const spaceNode = document.createTextNode(` ${after}`);

      const parent = textNode.parentNode;
      if (parent) {
        parent.insertBefore(mentionSpan, textNode.nextSibling);
        parent.insertBefore(spaceNode, mentionSpan.nextSibling);
      }

      // Posicionar cursor apos o espaco
      const newRange = document.createRange();
      newRange.setStart(spaceNode, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }

    // Extrair valor raw e atualizar state
    const rawValue = extractRawValue(editorEl);
    setNewComment(rawValue);
    setMentionSearch(null);
    setMentionUsers([]);
  }, []);

  const handleCommentKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (mentionSearch !== null && mentionUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex(i => Math.min(i + 1, mentionUsers.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex(i => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(mentionUsers[mentionIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMentionSearch(null);
          return;
        }
      }
      // Enter normal (sem shift) envia comentário
      if (e.key === 'Enter' && !e.shiftKey && mentionSearch === null) {
        e.preventDefault();
        handleAddComment();
      }
    },
    [mentionSearch, mentionUsers, mentionIndex, insertMention, handleAddComment],
  );

  const formatCommentDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (isOpen && card) {
      setTitulo(card.titulo);
      setDescricao(card.descricao || '');
      setPrioridade(card.prioridade);
      setExpiraEm(card.expira_em ? moment.utc(card.expira_em).format('YYYY-MM-DD') : '');
      setIsEditing(false);
      setTabIndex(0);
      const cv: Record<number, any> = {};
      (card.campo_valores || []).forEach(campo => {
        cv[campo.campo_id] = campo.valor;
      });
      setCampoValues(cv);
      setSelectedUsers(usuariosToOptions(card.usuarios));
      setSelectedSupervisors(usuariosToOptions(card.supervisores));
    }
  }, [isOpen, card]);

  // O comentário em digitação só é descartado ao abrir ou trocar de card: o mesmo card
  // relido (o quadro recalcula os dias a cada leitura) não apaga o que se está digitando.
  const cardId = card?.id;
  useEffect(() => {
    if (isOpen) setNewComment('');
  }, [isOpen, cardId]);

  // Entrega ao domínio o envio do comentário pendente. A falha já avisa pelo onError do
  // envio e não barra a ação do domínio; o texto continua no campo.
  const enviarComentario = createCommentMutation.mutateAsync;
  useEffect(() => {
    if (!onComentarioPendente) return undefined;
    onComentarioPendente(async () => {
      const texto = newComment.trim();
      if (!texto || !card || isReadOnly || enviandoComentarioRef.current) return;
      await enviarComentario(texto).catch(() => undefined);
    });
    return () => onComentarioPendente(null);
  }, [onComentarioPendente, newComment, card, isReadOnly, enviarComentario]);

  const buildCampoValores = (): IKanbanCampoValorInput[] =>
    Object.entries(campoValues)
      .filter(([, val]) => val !== null && val !== undefined && val !== '')
      .map(([campoIdStr, val]) => {
        const campoId = parseInt(campoIdStr, 10);
        const campo = campos.find(c => c.id === campoId);
        const input: IKanbanCampoValorInput = { campo_id: campoId };
        if (!campo) return input;
        if (campo.tipo === 'texto' || campo.tipo === 'select') input.valor_texto = String(val);
        else if (campo.tipo === 'numero' || campo.tipo === 'moeda')
          input.valor_numero = Number(val);
        else if (campo.tipo === 'data') input.valor_data = String(val);
        else if (campo.tipo === 'checkbox') input.valor_boolean = Boolean(val);
        return input;
      });

  const handleSave = () => {
    if (isReadOnly) return; // blindagem: espelho de módulo nunca persiste
    // Responsável é obrigatório (>=1) também na EDIÇÃO — espelha o guard do modal
    // de criação. Sem isso o card poderia ficar sem responsável e só estourar no
    // submit (o backend barra via _valida_vinculos_update, mas prevenir é melhor).
    // Com a seção de responsáveis escondida (o domínio tem o seu), a regra não se aplica.
    if (card && titulo.trim() && isCreator && !hideResponsaveis && selectedUsers.length === 0) {
      toast({
        title: 'Selecione ao menos um responsável.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (card && titulo.trim() && isCreator) {
      const campoValores = buildCampoValores();
      const userIds = selectedUsers.map(u => u.id);
      // Vínculos com papel: responsáveis + supervisores.
      const vinculos: IKanbanVinculo[] = [
        ...selectedUsers.map(u => ({ user_id: u.id, papel: 'responsavel' as const })),
        ...selectedSupervisors.map(u => ({ user_id: u.id, papel: 'supervisor' as const })),
      ];
      onSave(
        card.id,
        titulo.trim(),
        descricao,
        prioridade,
        expiraEm || null,
        campoValores.length > 0 ? campoValores : undefined,
        userIds.length > 0 ? userIds : undefined,
        vinculos,
      );
      setIsEditing(false);
      onClose();
    }
  };

  const toggleUser = (u: IKanbanUserOption) => {
    setSelectedUsers(prev =>
      prev.some(x => x.id === u.id) ? prev.filter(x => x.id !== u.id) : [...prev, u],
    );
  };

  // Supervisor não pode ser também responsável no mesmo card — ao marcar, sai dos responsáveis.
  const toggleSupervisor = (u: IKanbanUserOption) => {
    setSelectedSupervisors(prev => {
      if (prev.some(x => x.id === u.id)) return prev.filter(x => x.id !== u.id);
      setSelectedUsers(r => r.filter(x => x.id !== u.id));
      return [...prev, u];
    });
  };

  const handleArchive = () => {
    if (isReadOnly) return; // blindagem: espelho de módulo não arquiva
    // eslint-disable-next-line no-alert
    if (!card || !isCreator || !window.confirm('Tem certeza que deseja arquivar este card?'))
      return;
    onArchive(card.id);
    onClose();
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const handleStartEditing = () => {
    if (isCreator) setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (card) {
      setTitulo(card.titulo);
      setDescricao(card.descricao || '');
      setPrioridade(card.prioridade);
      setExpiraEm(card.expira_em ? moment.utc(card.expira_em).format('YYYY-MM-DD') : '');
      const cv: Record<number, any> = {};
      (card.campo_valores || []).forEach(campo => {
        cv[campo.campo_id] = campo.valor;
      });
      setCampoValues(cv);
      setSelectedUsers(usuariosToOptions(card.usuarios));
      setSelectedSupervisors(usuariosToOptions(card.supervisores));
    }
    setIsEditing(false);
  };

  const formatCampoDisplay = (campo: any, cv: any, hasValue: boolean) => {
    if (!hasValue) return '-';
    if (campo.tipo === 'moeda')
      return `R$ ${Number(cv.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    if (campo.tipo === 'checkbox') return cv.valor ? 'Sim' : 'Nao';
    if (campo.tipo === 'data' && typeof cv.valor === 'string')
      return cv.valor.split('-').reverse().join('/');
    return String(cv.valor);
  };

  if (!card) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={size} isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent
        borderRadius="2xl"
        mx={4}
        overflow="hidden"
        maxH="90vh"
        h={altura}
        position="relative"
      >
        <ModalCloseButton top={3} right={3} borderRadius="full" zIndex={2} size="sm" />

        <ModalBody p={0} display="flex" flexDirection={{ base: 'column', md: 'row' }}>
          {/* Coluna principal (esquerda) */}
          <Flex flex={1} p={6} direction="column" overflowY="auto" maxH="90vh">
            {/* Titulo */}
            <Flex alignItems="flex-start" gap={2} mb={cabecalho ? 2 : 4}>
              {isEditing ? (
                <Input
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                  fontSize="xl"
                  fontWeight="700"
                  variant="flushed"
                  color="gray.900"
                  borderColor="blue.300"
                  autoFocus
                />
              ) : (
                <Text flex={1} fontSize="xl" fontWeight="700" color="gray.900" pr={8}>
                  {card.titulo}
                </Text>
              )}
              {!isEditing && tituloAcao}
              {!isEditing && isCreator && !hideEdit && (
                <IconButton
                  icon={<PencilSimple size={16} />}
                  aria-label="Editar"
                  size="sm"
                  variant="ghost"
                  borderRadius="lg"
                  color="gray.400"
                  _hover={{ bg: 'gray.100', color: 'gray.600' }}
                  onClick={handleStartEditing}
                />
              )}
              {!isEditing && !isCreator && !hideEdit && (
                <Tooltip
                  label="Apenas o criador pode editar"
                  hasArrow
                  bg="gray.700"
                  fontSize="11px"
                >
                  <Badge
                    colorScheme="gray"
                    variant="subtle"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    px={2}
                    py={1}
                    borderRadius="lg"
                    fontSize="10px"
                  >
                    <Lock size={10} />
                    Leitura
                  </Badge>
                </Tooltip>
              )}
            </Flex>
            {cabecalho && <Box mb={4}>{cabecalho}</Box>}

            {/* Descricao */}
            <Box mb={5} display={hideDescription ? 'none' : undefined}>
              <Flex alignItems="center" gap={1.5} mb={2}>
                <TextAlignLeft size={14} color="#718096" weight="bold" />
                <Text
                  fontSize="12px"
                  fontWeight="600"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Descricao
                </Text>
              </Flex>

              {isEditing ? (
                <Tabs index={tabIndex} onChange={setTabIndex} size="sm" variant="soft-rounded">
                  <TabList mb={2}>
                    <Tab
                      fontSize="12px"
                      px={3}
                      py={1}
                      _selected={{ bg: 'gray.100', color: 'gray.800' }}
                    >
                      Escrever
                    </Tab>
                    <Tab
                      fontSize="12px"
                      px={3}
                      py={1}
                      _selected={{ bg: 'gray.100', color: 'gray.800' }}
                    >
                      Preview
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel p={0}>
                      <Textarea
                        value={descricao}
                        onChange={e => setDescricao(e.target.value)}
                        placeholder="Descreva o card usando Markdown..."
                        minH="150px"
                        borderRadius="xl"
                        fontSize="13px"
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
                        }}
                      />
                    </TabPanel>
                    <TabPanel p={0}>
                      <Box
                        minH="150px"
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="xl"
                        bg="gray.50"
                      >
                        {descricao ? (
                          <MarkdownRenderer content={descricao} />
                        ) : (
                          <Text color="gray.300" fontSize="13px">
                            Nenhum conteudo
                          </Text>
                        )}
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              ) : (
                <Box
                  p={3}
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="xl"
                  minH="60px"
                  bg="gray.50"
                  cursor={isCreator ? 'pointer' : 'default'}
                  onClick={handleStartEditing}
                  _hover={isCreator ? { borderColor: 'blue.200' } : {}}
                  transition="border-color 0.15s"
                >
                  {card.descricao ? (
                    <Box fontSize="13px" color="gray.700">
                      <MarkdownRenderer content={card.descricao} />
                    </Box>
                  ) : (
                    <Text color="gray.300" fontSize="13px">
                      {isCreator ? 'Clique para adicionar...' : 'Sem descricao'}
                    </Text>
                  )}
                </Box>
              )}
            </Box>

            {/* Botoes de edicao fixos acima do divider */}
            {isEditing && (
              <Flex
                gap={2}
                mb={5}
                p={3}
                bg="blue.50"
                borderRadius="xl"
                border="1px solid"
                borderColor="blue.100"
                alignItems="center"
              >
                <Text fontSize="12px" color="blue.600" flex={1} fontWeight="500">
                  Modo de edicao ativo
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  borderRadius="lg"
                  fontSize="12px"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
                <Button
                  size="xs"
                  leftIcon={<Check size={12} weight="bold" />}
                  bg="blue.600"
                  color="white"
                  borderRadius="lg"
                  fontSize="12px"
                  fontWeight="500"
                  _hover={{ bg: 'blue.700' }}
                  onClick={handleSave}
                  isDisabled={!hideResponsaveis && selectedUsers.length === 0}
                  title={
                    !hideResponsaveis && selectedUsers.length === 0
                      ? 'Selecione ao menos um responsável'
                      : undefined
                  }
                >
                  Salvar
                </Button>
              </Flex>
            )}

            <Divider mb={4} />

            {/* Abas do card. A aba extra do domínio (ex.: "Estatuto") vem PRIMEIRA — é o
                  conteúdo principal p/ esses módulos; as fixas (Comentários/Anexos/etc.) seguem. */}
            <Flex gap={0} mb={4} borderBottom="1px solid" borderColor="gray.100">
              {extraTab && (
                // Mesma caixa das abas fixas (unstyled + pb): com `ghost` + `py` o rótulo
                // descia 8px em relação a Comentários/Anexos/Atividade.
                <Button
                  variant="unstyled"
                  size="sm"
                  px={3}
                  pb={2}
                  borderBottom="2px solid"
                  borderColor={activeTab === extraTab.key ? 'blue.500' : 'transparent'}
                  borderRadius={0}
                  color={activeTab === extraTab.key ? 'blue.600' : 'gray.400'}
                  fontWeight={activeTab === extraTab.key ? '600' : '500'}
                  fontSize="12px"
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  _hover={{ color: 'blue.500' }}
                  onClick={() => setActiveTab(extraTab.key)}
                >
                  {extraTab.icon}
                  {extraTab.label}
                </Button>
              )}
              <Button
                variant="unstyled"
                size="sm"
                px={3}
                pb={2}
                borderBottom="2px solid"
                borderColor={activeTab === 'comentarios' ? 'gray.900' : 'transparent'}
                borderRadius={0}
                color={activeTab === 'comentarios' ? 'gray.800' : 'gray.400'}
                fontWeight={activeTab === 'comentarios' ? '600' : '500'}
                fontSize="12px"
                display="flex"
                alignItems="center"
                gap={1.5}
                _hover={{ color: 'gray.600' }}
                onClick={() => setActiveTab('comentarios')}
              >
                <ChatCircle size={13} weight="bold" />
                Comentarios ({comments.length})
              </Button>
              <Button
                variant="unstyled"
                size="sm"
                px={3}
                pb={2}
                borderBottom="2px solid"
                borderColor={activeTab === 'anexos' ? 'gray.900' : 'transparent'}
                borderRadius={0}
                color={activeTab === 'anexos' ? 'gray.800' : 'gray.400'}
                fontWeight={activeTab === 'anexos' ? '600' : '500'}
                fontSize="12px"
                display="flex"
                alignItems="center"
                gap={1.5}
                _hover={{ color: 'gray.600' }}
                onClick={() => setActiveTab('anexos')}
              >
                <Paperclip size={13} weight="bold" />
                Anexos ({anexos.length})
              </Button>
              <Button
                variant="unstyled"
                size="sm"
                px={3}
                pb={2}
                borderBottom="2px solid"
                borderColor={activeTab === 'atividade' ? 'gray.900' : 'transparent'}
                borderRadius={0}
                color={activeTab === 'atividade' ? 'gray.800' : 'gray.400'}
                fontWeight={activeTab === 'atividade' ? '600' : '500'}
                fontSize="12px"
                display="flex"
                alignItems="center"
                gap={1.5}
                _hover={{ color: 'gray.600' }}
                onClick={() => setActiveTab('atividade')}
              >
                <ClockCounterClockwise size={13} weight="bold" />
                Atividade
              </Button>
              <Button
                variant="unstyled"
                size="sm"
                px={3}
                pb={2}
                borderBottom="2px solid"
                borderColor={activeTab === 'assinatura' ? 'purple.500' : 'transparent'}
                borderRadius={0}
                color={activeTab === 'assinatura' ? 'purple.600' : 'gray.400'}
                fontWeight={activeTab === 'assinatura' ? '600' : '500'}
                fontSize="12px"
                display={hideAssinatura ? 'none' : 'flex'}
                alignItems="center"
                gap={1.5}
                _hover={{ color: 'purple.500' }}
                onClick={() => setActiveTab('assinatura')}
              >
                <PenNib size={13} weight="bold" />
                Assinatura
              </Button>
            </Flex>

            {/* Conteudo da aba extra do domínio */}
            {extraTab && activeTab === extraTab.key && (
              <Flex direction="column" flex={1} minH={0} overflowY="auto">
                {extraTab.content}
              </Flex>
            )}

            {/* Conteudo da aba */}
            {activeTab === 'comentarios' && (
              <Flex direction="column" flex={1} minH={0}>
                {/* Input com @menções — escondido no espelho read-only (só visualiza) */}
                {!isReadOnly && (
                  <Box position="relative" mb={4}>
                    <MentionTextarea
                      ref={commentRef}
                      value={newComment}
                      onValueChange={handleCommentChange}
                      onKeyDown={handleCommentKeyDown}
                      placeholder="Escreva um comentario... Use @ para mencionar"
                    />
                    {/* Dropdown de menções */}
                    {mentionSearch !== null && mentionUsers.length > 0 && (
                      <Box
                        position="absolute"
                        bottom="100%"
                        left={0}
                        w="100%"
                        bg="white"
                        borderRadius="12px"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.100"
                        py={1}
                        mb={1}
                        maxH="180px"
                        overflowY="auto"
                        overflowX="hidden"
                        zIndex={10}
                      >
                        {mentionUsers.map((u, idx) => (
                          <Flex
                            key={u.id}
                            px={3}
                            py={1.5}
                            align="center"
                            gap={2}
                            cursor="pointer"
                            bg={idx === mentionIndex ? 'blue.50' : 'transparent'}
                            _hover={{ bg: 'gray.50' }}
                            onClick={() => insertMention(u)}
                          >
                            <Avatar size="2xs" name={u.nome} />
                            <Box flex={1} minW={0}>
                              <Text fontSize="12px" fontWeight="600" color="gray.700" noOfLines={1}>
                                {u.nome}
                              </Text>
                              <Text fontSize="10px" color="gray.400" noOfLines={1}>
                                {u.email}
                              </Text>
                            </Box>
                          </Flex>
                        ))}
                      </Box>
                    )}
                    <IconButton
                      aria-label="Enviar"
                      icon={<PaperPlaneTilt size={14} weight="fill" />}
                      bg="gray.900"
                      color="white"
                      size="xs"
                      borderRadius="md"
                      position="absolute"
                      right="8px"
                      bottom="8px"
                      _hover={{ bg: 'gray.800' }}
                      onClick={handleAddComment}
                      isLoading={createCommentMutation.isPending}
                      isDisabled={!newComment.trim() || createCommentMutation.isPending}
                    />
                  </Box>
                )}

                {/* Lista */}
                <Box
                  flex={1}
                  minH={0}
                  overflowY="auto"
                  css={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
                  }}
                >
                  {isLoadingComments && (
                    <Flex justify="center" py={4}>
                      <Spinner size="sm" color="gray.300" />
                    </Flex>
                  )}
                  {!isLoadingComments && erroNosComentarios && (
                    <Text fontSize="12px" color="red.400" textAlign="center" py={4}>
                      Não foi possível carregar os comentários.
                    </Text>
                  )}
                  {!isLoadingComments &&
                    !erroNosComentarios &&
                    !comentariosDoMaisNovo &&
                    comentariosAnteriores}
                  {!isLoadingComments &&
                    !erroNosComentarios &&
                    comments.length === 0 &&
                    !comentariosAnteriores && (
                      <Text fontSize="12px" color="gray.300" textAlign="center" py={4}>
                        Nenhum comentario ainda
                      </Text>
                    )}
                  {!isLoadingComments &&
                    comentariosNaOrdem.map(comment => (
                      <Flex key={comment.id} gap={2.5} mb={3}>
                        <Avatar size="xs" name={comment.usuario.nome} mt={0.5} />
                        <Box flex={1}>
                          <Flex alignItems="baseline" gap={2}>
                            <Text fontSize="12px" fontWeight="600" color="gray.700">
                              {comment.usuario.nome}
                            </Text>
                            <Text fontSize="10px" color="gray.400">
                              {formatCommentDate(comment.dt_criacao)}
                            </Text>
                          </Flex>
                          <Box
                            fontSize="13px"
                            color="gray.600"
                            mt={0.5}
                            bg="gray.50"
                            p={2}
                            borderRadius="lg"
                          >
                            <MarkdownRenderer content={comment.descricao} />
                          </Box>
                        </Box>
                      </Flex>
                    ))}
                  {!isLoadingComments &&
                    !erroNosComentarios &&
                    comentariosDoMaisNovo &&
                    comentariosAnteriores}
                </Box>
              </Flex>
            )}

            {/* Aba Anexos — conteúdo do domínio, quando houver; senão a lista genérica. */}
            {activeTab === 'anexos' && anexosContent}
            {activeTab === 'anexos' && !anexosContent && (
              <Box>
                {/* Upload — escondido no espelho read-only (só visualiza/baixa) */}
                {!isReadOnly && (
                  <Box mb={4}>
                    <input
                      id="kanban-file-upload"
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Paperclip size={14} weight="bold" />}
                      borderRadius="lg"
                      fontSize="12px"
                      fontWeight="500"
                      borderColor="gray.200"
                      color="gray.600"
                      _hover={{ bg: 'gray.50' }}
                      isLoading={uploadAnexoMutation.isPending}
                      onClick={() => document.getElementById('kanban-file-upload')?.click()}
                    >
                      Adicionar arquivo
                    </Button>
                    <Text fontSize="10px" color="gray.400" mt={1}>
                      Max 10MB por arquivo
                    </Text>
                  </Box>
                )}

                {/* Lista */}
                {isLoadingAnexos && (
                  <Flex justify="center" py={4}>
                    <Spinner size="sm" color="gray.300" />
                  </Flex>
                )}
                {!isLoadingAnexos && anexos.length === 0 && (
                  <Text fontSize="12px" color="gray.300" textAlign="center" py={4}>
                    Nenhum anexo
                  </Text>
                )}
                {!isLoadingAnexos && anexos.length > 0 && (
                  <Flex direction="column" gap={2}>
                    {anexos.map(anexo => {
                      const Icon = getFileIcon(anexo.content_type);
                      const isRejected = anexo.autentique_status === 'cancelled';
                      const envioCancelado = anexo.autentique_status === 'cancelled_by_user';
                      return (
                        <Flex
                          key={anexo.id}
                          alignItems="center"
                          gap={2.5}
                          p={2.5}
                          bg="gray.50"
                          borderRadius="lg"
                          border="1px solid"
                          borderColor={isRejected ? 'red.100' : 'gray.100'}
                          borderLeft={isRejected ? '3px solid' : '1px solid'}
                          borderLeftColor={isRejected ? 'red.300' : 'gray.100'}
                          _hover={{ borderColor: isRejected ? 'red.200' : 'gray.200' }}
                          transition="border-color 0.1s"
                        >
                          <Flex
                            w="32px"
                            h="32px"
                            borderRadius="lg"
                            bg="white"
                            border="1px solid"
                            borderColor={isRejected ? 'red.200' : 'gray.200'}
                            align="center"
                            justify="center"
                            flexShrink={0}
                          >
                            <Icon size={16} color={isRejected ? '#E53E3E' : '#718096'} />
                          </Flex>
                          <Box flex={1} minW={0}>
                            <Flex alignItems="center" gap={1.5}>
                              <Text
                                fontSize="12px"
                                fontWeight="500"
                                color={isRejected ? 'gray.500' : 'gray.700'}
                                noOfLines={1}
                                textDecoration={isRejected ? 'line-through' : 'none'}
                              >
                                {anexo.nome}
                              </Text>
                              {isRejected && (
                                <Badge
                                  fontSize="8px"
                                  colorScheme="red"
                                  variant="subtle"
                                  borderRadius="full"
                                  px={1.5}
                                  flexShrink={0}
                                >
                                  Rejeitado
                                </Badge>
                              )}
                              {envioCancelado && (
                                <Badge
                                  fontSize="8px"
                                  colorScheme="gray"
                                  variant="subtle"
                                  borderRadius="full"
                                  px={1.5}
                                  flexShrink={0}
                                >
                                  Envio cancelado
                                </Badge>
                              )}
                            </Flex>
                            <Text fontSize="10px" color="gray.400">
                              {formatFileSize(anexo.tamanho)}
                              {anexo.usuario_nome && ` · ${anexo.usuario_nome}`}
                            </Text>
                          </Box>
                          <Flex gap={1}>
                            <IconButton
                              aria-label="Download"
                              icon={<DownloadSimple size={14} />}
                              size="xs"
                              variant="ghost"
                              color="gray.400"
                              _hover={{ color: 'blue.500', bg: 'blue.50' }}
                              onClick={() => window.open(anexo.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <IconButton
                                aria-label="Remover"
                                icon={<Trash size={14} />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'red.500', bg: 'red.50' }}
                                isLoading={deleteAnexoMutation.isPending}
                                isDisabled={deleteAnexoMutation.isPending}
                                onClick={() => {
                                  // eslint-disable-next-line no-alert
                                  if (window.confirm('Remover este anexo?')) {
                                    deleteAnexoMutation.mutate(anexo.id);
                                  }
                                }}
                              />
                            )}
                          </Flex>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
              </Box>
            )}

            {/* Aba Atividade. Com `altura` fixa, a lista ocupa a aba inteira (senão ficava
                presa em 300px com a metade de baixo do modal vazia). */}
            {activeTab === 'atividade' && (
              <Box
                {...(altura ? { flex: 1, minH: 0, display: 'flex', flexDirection: 'column' } : {})}
              >
                {isLoadingAtividades && (
                  <Flex justify="center" py={6}>
                    <Spinner size="sm" color="gray.300" />
                  </Flex>
                )}
                {!isLoadingAtividades && atividades.length === 0 && (
                  <Text fontSize="12px" color="gray.300" textAlign="center" py={6}>
                    Nenhuma atividade registrada
                  </Text>
                )}
                {!isLoadingAtividades && atividades.length > 0 && (
                  <Box
                    {...(altura ? { flex: 1, minH: 0 } : { maxH: '300px' })}
                    overflowY="auto"
                    css={{
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-track': { background: 'transparent' },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#CBD5E0',
                        borderRadius: '2px',
                      },
                    }}
                  >
                    {atividades.map(atv => (
                      <Flex key={atv.id} gap={2.5} mb={3} alignItems="flex-start">
                        <Flex
                          w="24px"
                          h="24px"
                          borderRadius="full"
                          bg="gray.100"
                          align="center"
                          justify="center"
                          flexShrink={0}
                          mt={0.5}
                        >
                          <ClockCounterClockwise size={12} color="#A0AEC0" />
                        </Flex>
                        <Box flex={1}>
                          <Text fontSize="12px" color="gray.600" overflowWrap="anywhere">
                            <Text as="span" fontWeight="600" color="gray.700">
                              {atv.usuario?.nome || 'Sistema'}
                            </Text>{' '}
                            {continuarFrase(atv.descricao)}
                          </Text>
                          <Text fontSize="10px" color="gray.400" mt={0.5}>
                            {moment(atv.dt_criacao).format('DD/MM/YYYY HH:mm')}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 'assinatura' && (
              <Box>
                {isLoadingAssinatura && (
                  <Flex justify="center" py={6}>
                    <Spinner size="sm" color="purple.300" />
                  </Flex>
                )}
                {!isLoadingAssinatura && assinaturaDocs.length === 0 && (
                  <Text fontSize="12px" color="gray.300" textAlign="center" py={6}>
                    Nenhum documento enviado para assinatura
                  </Text>
                )}
                {!isLoadingAssinatura &&
                  (podeBaixarRelatorio || (podeManterEnvio && aReenviar > 0)) && (
                    <Flex gap={2} wrap="wrap" mb={3}>
                      {podeBaixarRelatorio && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="purple"
                          leftIcon={<DownloadSimple size={12} />}
                          isLoading={baixarRelatorioMutation.isPending}
                          onClick={() => baixarRelatorioMutation.mutate()}
                        >
                          Baixar relatório (PDF)
                        </Button>
                      )}
                      {podeManterEnvio && aReenviar > 0 && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="purple"
                          leftIcon={<PaperPlaneTilt size={12} />}
                          isLoading={reenviarMutation.isPending}
                          onClick={() => reenviarMutation.mutate()}
                        >
                          Reenviar aos pendentes ({aReenviar})
                        </Button>
                      )}
                    </Flex>
                  )}
                {/* Com termo digitado o campo fica: a lista segue filtrada mesmo se a releitura falhar. */}
                {!isLoadingAssinatura && (temListaLonga || !!buscaAssinante) && (
                  <InputGroup size="sm" mb={3}>
                    <InputLeftElement pointerEvents="none">
                      <MagnifyingGlass size={14} color="gray" />
                    </InputLeftElement>
                    <Input
                      borderRadius="md"
                      placeholder="Buscar por nome ou e-mail"
                      aria-label="Buscar assinante por nome ou e-mail"
                      value={buscaAssinante}
                      onChange={e => setBuscaAssinante(e.target.value)}
                    />
                  </InputGroup>
                )}
                {!isLoadingAssinatura &&
                  assinaturaDocs.map(doc => (
                    <Box key={doc.documento_id} mb={4}>
                      <Flex align="center" gap={2} mb={3}>
                        <PenNib size={14} color="#7C3AED" weight="bold" />
                        <Text fontSize="12px" fontWeight="600" color="gray.700">
                          {doc.nome}
                        </Text>
                        <Badge
                          fontSize="9px"
                          colorScheme={
                            {
                              signed: 'green',
                              cancelled: 'red',
                              cancelled_by_user: 'gray',
                            }[doc.status] || 'orange'
                          }
                          borderRadius="full"
                        >
                          {{
                            signed: 'Assinado',
                            cancelled: 'Rejeitado',
                            cancelled_by_user: 'Cancelado',
                          }[doc.status] || 'Pendente'}
                        </Badge>
                        {podeCancelarEnvio && doc.status === 'pending' && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            ml="auto"
                            leftIcon={<XCircle size={12} weight="bold" />}
                            onClick={() => setEnvioParaCancelar(doc)}
                          >
                            Cancelar envio
                          </Button>
                        )}
                      </Flex>

                      {/* Quem recusou, no topo: com dezenas de assinantes a recusa ficava no meio da lista. */}
                      {recusaNoTopo(doc) && (
                        <Box
                          mb={3}
                          p={3}
                          bg="red.50"
                          borderRadius="lg"
                          border="1px solid"
                          borderColor="red.100"
                        >
                          {doc.signatarios
                            .filter(sig => sig.status === 'rejeitado')
                            .map((sig, i) => {
                              const recusa = sig.eventos.find(ev => ev.tipo === 'rejeitado');
                              return (
                                // eslint-disable-next-line react/no-array-index-key -- assinante por SMS vem sem e-mail
                                <Box key={`${sig.email}-${i}`} mb={2}>
                                  <Text fontSize="12px" fontWeight="600" color="red.600">
                                    Recusado por {sig.nome}
                                    {recusa?.dt &&
                                      ` em ${moment(recusa.dt).format('DD/MM/YYYY HH:mm')}`}
                                  </Text>
                                  {sig.email && (
                                    <Text fontSize="10px" color="gray.500">
                                      {sig.email}
                                    </Text>
                                  )}
                                  {recusa && (
                                    <Text fontSize="11px" color="gray.700" mt={1}>
                                      {/* Sem o campo (back antigo), a descrição inteira. */}
                                      {recusa.motivo === undefined
                                        ? recusa.descricao
                                        : `Motivo: ${recusa.motivo || 'não informado'}`}
                                    </Text>
                                  )}
                                </Box>
                              );
                            })}
                        </Box>
                      )}

                      {/* Só com o documento assinado no banco: antes disso o arquivo da Autentique vem
                          parcial, e a lista dela pode ter perdido alguém (cancelado, removido no painel). */}
                      {doc.arquivo_assinado && doc.status === 'signed' && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="green"
                          leftIcon={<DownloadSimple size={12} />}
                          mb={3}
                          onClick={() => window.open(doc.arquivo_assinado, '_blank')}
                        >
                          Baixar documento assinado
                        </Button>
                      )}

                      {termoAssinante && !doc.erro && !assinantesDaAba(doc).length && (
                        <Text fontSize="12px" color="gray.400" textAlign="center" py={3}>
                          Ninguém encontrado neste documento.
                        </Text>
                      )}

                      {assinantesDaAba(doc).map((sig, i) => (
                        <Box
                          // eslint-disable-next-line react/no-array-index-key -- assinante por SMS vem sem e-mail
                          key={`${sig.email}-${i}`}
                          mb={3}
                          p={3}
                          bg="gray.50"
                          borderRadius="lg"
                          border="1px solid"
                          borderColor="gray.100"
                        >
                          <Flex align="center" gap={2} mb={2}>
                            <Avatar size="xs" name={sig.nome} />
                            <Box flex={1}>
                              <Text fontSize="12px" fontWeight="600" color="gray.700">
                                {sig.nome}
                              </Text>
                              {sig.email && (
                                <Text fontSize="10px" color="gray.400">
                                  {sig.email}
                                </Text>
                              )}
                            </Box>
                            {(() => {
                              const aberto = docAberto(doc);
                              const naoEntregue = aberto && emailNaoEntregue(sig);
                              const scheme =
                                { assinado: 'green', rejeitado: 'red' }[sig.status] ||
                                (naoEntregue && 'red') ||
                                (aberto ? 'yellow' : 'gray');
                              const label =
                                { assinado: 'Assinou', rejeitado: 'Rejeitou' }[sig.status] ||
                                (naoEntregue && 'E-mail não entregue') ||
                                (aberto ? 'Pendente' : 'Não assinou');
                              return (
                                <Badge fontSize="9px" borderRadius="full" colorScheme={scheme}>
                                  {label}
                                </Badge>
                              );
                            })()}
                            {podeManterEnvio &&
                              docAberto(doc) &&
                              sig.status !== 'assinado' &&
                              !!sig.signatario_id && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="purple"
                                  leftIcon={<PencilSimple size={12} />}
                                  onClick={() => setAssinanteParaCorrigir(sig)}
                                >
                                  Corrigir
                                </Button>
                              )}
                          </Flex>

                          {sig.eventos.length > 0 && (
                            <Box pl={2} borderLeft="2px solid" borderColor="gray.200">
                              {sig.eventos.map(ev => (
                                <Flex
                                  key={`${sig.email}-${ev.tipo}-${ev.dt || 'no-dt'}`}
                                  gap={2}
                                  mb={1.5}
                                  align="flex-start"
                                >
                                  <Flex
                                    w="16px"
                                    h="16px"
                                    borderRadius="full"
                                    bg={ev.icone === 'check' ? 'green.50' : 'red.50'}
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                    mt={0.5}
                                  >
                                    {ev.icone === 'check' ? (
                                      <CheckCircle size={10} color="#22C55E" weight="fill" />
                                    ) : (
                                      <XCircle size={10} color="#EF4444" weight="fill" />
                                    )}
                                  </Flex>
                                  <Box>
                                    <Text fontSize="11px" color="gray.600">
                                      {ev.descricao}
                                    </Text>
                                    {ev.dt && (
                                      <Text fontSize="9px" color="gray.400">
                                        Em {moment(ev.dt).format('DD/MM/YYYY HH:mm')}
                                      </Text>
                                    )}
                                  </Box>
                                </Flex>
                              ))}
                            </Box>
                          )}

                          {sig.sign_link && sig.status === 'pendente' && docAberto(doc) && (
                            <Box mt={2}>
                              <Flex
                                align="center"
                                gap={1}
                                bg="white"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                pl={2}
                                pr={1}
                                py={0.5}
                              >
                                <Text
                                  fontSize="10px"
                                  color="gray.600"
                                  flex={1}
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                >
                                  {sig.sign_link}
                                </Text>
                                <Tooltip
                                  label={linkCopiado === sig.sign_link ? 'Copiado!' : 'Copiar link'}
                                  fontSize="xs"
                                >
                                  <IconButton
                                    aria-label="Copiar link de assinatura"
                                    size="xs"
                                    variant="ghost"
                                    icon={
                                      linkCopiado === sig.sign_link ? (
                                        <Check size={12} weight="bold" color="#16A34A" />
                                      ) : (
                                        <Copy size={12} weight="bold" />
                                      )
                                    }
                                    onClick={() => copiarLink(sig.sign_link)}
                                  />
                                </Tooltip>
                                <Tooltip label="Abrir link" fontSize="xs">
                                  <IconButton
                                    aria-label="Abrir link de assinatura"
                                    size="xs"
                                    variant="ghost"
                                    icon={<ArrowSquareOut size={12} weight="bold" />}
                                    onClick={() =>
                                      window.open(
                                        sig.sign_link ?? '',
                                        '_blank',
                                        'noopener,noreferrer',
                                      )
                                    }
                                  />
                                </Tooltip>
                              </Flex>
                              <Text fontSize="9px" color="gray.400" mt={1}>
                                Link exclusivo deste assinante: não repasse a outra pessoa.
                              </Text>
                            </Box>
                          )}
                        </Box>
                      ))}

                      {doc.erro && (
                        <Text fontSize="10px" color="red.400" mt={1}>
                          Erro ao consultar: {doc.erro}
                        </Text>
                      )}
                    </Box>
                  ))}

                {card && (
                  <CorrigirAssinanteModal
                    cardId={card.id}
                    assinante={assinanteParaCorrigir}
                    onClose={() => setAssinanteParaCorrigir(null)}
                    onCorrigido={() => {
                      toast({ title: 'Assinante corrigido.', status: 'success' });
                      setAssinanteParaCorrigir(null);
                      atualizarAssinatura();
                    }}
                    onFalhou={atualizarAssinatura}
                  />
                )}

                <Modal
                  isOpen={!!envioParaCancelar}
                  onClose={() => setEnvioParaCancelar(null)}
                  size="md"
                  isCentered
                >
                  <ModalOverlay />
                  <ModalContent borderRadius="xl">
                    <ModalHeader fontSize="16px">Cancelar envio para assinatura?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Text fontSize="13px" color="gray.600">
                        Quem ainda não assinou não consegue mais assinar no Autentique. O contrato e
                        o card voltam para CONTRATO.
                        {pendentes.length > 1 &&
                          ` Este card tem ${pendentes.length} envios pendentes: todos são cancelados.`}
                      </Text>
                      {jaAssinaram.length > 0 && (
                        <Box
                          mt={3}
                          p={3}
                          bg="orange.50"
                          border="1px solid"
                          borderColor="orange.200"
                          borderRadius="lg"
                        >
                          <Text fontSize="13px" color="orange.800" fontWeight="600">
                            {jaAssinaram.join(', ')} já{' '}
                            {jaAssinaram.length > 1 ? 'assinaram' : 'assinou'}.
                          </Text>
                          <Text fontSize="12px" color="orange.700" mt={1}>
                            A assinatura continua registrada no Autentique, mas este envio fica
                            cancelado.
                          </Text>
                        </Box>
                      )}
                      <Text fontSize="12px" color="gray.500" mt={3}>
                        O documento gerado continua nos anexos para corrigir e enviar de novo.
                      </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                      <Button size="sm" variant="ghost" onClick={() => setEnvioParaCancelar(null)}>
                        Voltar
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        isLoading={cancelarEnvioMutation.isPending}
                        onClick={() => cancelarEnvioMutation.mutate()}
                      >
                        Cancelar envio
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              </Box>
            )}
          </Flex>

          {/* Sidebar (direita) */}
          <Box
            w={{ base: '100%', md: '260px' }}
            bg="gray.50"
            pt={10}
            px={5}
            pb={5}
            flexShrink={0}
            overflowY="auto"
            maxH="90vh"
          >
            {/* Prioridade */}
            <Box mb={4} display={hidePriority ? 'none' : undefined}>
              <Text
                fontSize="11px"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.05em"
                mb={1.5}
              >
                Prioridade
              </Text>
              {isEditing ? (
                <Select
                  value={prioridade}
                  onChange={e => setPrioridade(e.target.value as KanbanPrioridade)}
                  size="sm"
                  borderRadius="lg"
                  fontSize="13px"
                  bg="white"
                >
                  <option value="baixa">Baixa</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </Select>
              ) : (
                <KanbanPriorityBadge prioridade={card.prioridade} size="md" />
              )}
            </Box>

            {/* Data de expiracao */}
            <Box mb={4} display={hideExpiracao ? 'none' : undefined}>
              <Flex alignItems="center" gap={1} mb={1.5}>
                <CalendarBlank size={12} color="#A0AEC0" weight="bold" />
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Expiracao
                </Text>
              </Flex>
              {isEditing ? (
                <KanbanDatePicker value={expiraEm} onChange={setExpiraEm} />
              ) : (
                <Text fontSize="13px" color={card.expira_em ? 'gray.700' : 'gray.400'}>
                  {card.expira_em ? moment.utc(card.expira_em).format('DD/MM/YYYY') : 'Sem data'}
                </Text>
              )}
            </Box>

            {/* Responsaveis (obrigatorio) + Supervisores (opcional) */}
            <Box mb={4} display={hideResponsaveis ? 'none' : undefined}>
              {isEditing ? (
                <Flex direction="column" gap={4}>
                  <UserPapelPicker
                    label="Responsaveis"
                    papel="responsavel"
                    selected={selectedUsers}
                    onToggle={toggleUser}
                    obrigatorio
                    departamentoIds={departamentoIds}
                  />
                  <UserPapelPicker
                    label="Supervisores"
                    papel="supervisor"
                    selected={selectedSupervisors}
                    onToggle={toggleSupervisor}
                    departamentoIds={departamentoIds}
                  />
                </Flex>
              ) : (
                <Flex direction="column" gap={3}>
                  {/* Responsaveis (view) */}
                  <Box>
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      mb={1.5}
                    >
                      Responsaveis
                    </Text>
                    {card.usuarios && card.usuarios.length > 0 ? (
                      <Flex direction="column" gap={1.5}>
                        {card.usuarios.map(u => (
                          <Flex key={u.id} alignItems="center" gap={2}>
                            <Avatar size="xs" name={u.nome || 'U'} />
                            <Text fontSize="12px" color="gray.600" fontWeight="500">
                              {u.nome}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="12px" color="gray.300">
                        Sem responsaveis
                      </Text>
                    )}
                  </Box>
                  {/* Supervisores (view) — só aparece se houver */}
                  {card.supervisores && card.supervisores.length > 0 && (
                    <Box>
                      <Text
                        fontSize="11px"
                        fontWeight="600"
                        color="purple.400"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        mb={1.5}
                      >
                        Supervisores
                      </Text>
                      <Flex direction="column" gap={1.5}>
                        {card.supervisores.map(u => (
                          <Flex key={u.id} alignItems="center" gap={2}>
                            <Avatar
                              size="xs"
                              name={u.nome || 'U'}
                              border="2px solid"
                              borderColor="purple.400"
                            />
                            <Text fontSize="12px" color="gray.600" fontWeight="500">
                              {u.nome}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    </Box>
                  )}
                </Flex>
              )}
            </Box>

            {/* Slot de ações do domínio (ex.: Estatutos) — injetado pela tela que usa o
                  modal genérico. O genérico não conhece o domínio (Open/Closed). */}
            {sidebarSlot && (
              <>
                {/* O divisor separa o slot do que vem acima; sem nada acima, sobraria solto. */}
                {!(hidePriority && hideExpiracao && hideResponsaveis) && <Divider my={3} />}
                {sidebarSlot}
              </>
            )}

            {/* Campos personalizaveis */}
            {campos.length > 0 && (
              <>
                <Divider my={3} />
                <Box>
                  <Flex alignItems="center" gap={1} mb={2}>
                    <Tag size={12} color="#A0AEC0" weight="bold" />
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Campos
                    </Text>
                  </Flex>
                  {isEditing ? (
                    <Flex direction="column" gap={2.5}>
                      {campos.map(campo => (
                        <KanbanCustomFieldInput
                          key={campo.id}
                          campo={campo}
                          value={campoValues[campo.id] ?? null}
                          onChange={value =>
                            setCampoValues(prev => ({ ...prev, [campo.id]: value }))
                          }
                          size="sm"
                        />
                      ))}
                    </Flex>
                  ) : (
                    <Flex direction="column" gap={2}>
                      {campos.map(campo => {
                        const cv = (card.campo_valores || []).find(v => v.campo_id === campo.id);
                        const hasValue =
                          cv && cv.valor !== null && cv.valor !== undefined && cv.valor !== '';
                        return (
                          <Box key={campo.id}>
                            <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                              {campo.nome}
                            </Text>
                            <Text fontSize="13px" color={hasValue ? 'gray.700' : 'gray.300'}>
                              {formatCampoDisplay(campo, cv, !!hasValue)}
                            </Text>
                          </Box>
                        );
                      })}
                    </Flex>
                  )}
                </Box>
              </>
            )}

            {/* Relatório de assinaturas ao supervisor: seção recolhível, acima do contrato (mais à vista). */}
            {isEventProject && mostraRelatorio && (
              <>
                <Divider my={3} />
                <SecaoRelatorio
                  value={relatorio}
                  onChange={setRelatorio}
                  alterado={relatorioMudou}
                  salvar={{
                    rotulo: 'Salvar relatório',
                    onSalvar: valor => salvarRelatorioMutation.mutate(valor),
                    isLoading: salvarRelatorioMutation.isPending,
                    isDisabled: !relatorioMudou,
                  }}
                />
              </>
            )}

            {/* Dados do Contrato (evento) */}
            {isEventProject && contratoInfo && (
              <>
                <Divider my={3} />
                <Box>
                  <Flex alignItems="center" gap={1} mb={2}>
                    <FileDoc size={12} color="#A0AEC0" weight="bold" />
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Contrato
                    </Text>
                  </Flex>

                  <Flex direction="column" gap={2.5}>
                    {/* CNPJ/CPF */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        CNPJ/CPF do contratado
                      </Text>
                      <Input
                        size="sm"
                        fontSize="12px"
                        borderRadius="md"
                        value={contratoCnpj}
                        onChange={e => {
                          const raw = e.target.value.replace(/\D/g, '');
                          let formatted = raw;
                          if (raw.length <= 11) {
                            formatted = raw
                              .replace(/(\d{3})(\d)/, '$1.$2')
                              .replace(/(\d{3})(\d)/, '$1.$2')
                              .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                          } else {
                            formatted = raw
                              .replace(/^(\d{2})(\d)/, '$1.$2')
                              .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                              .replace(/\.(\d{3})(\d)/, '.$1/$2')
                              .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
                          }
                          setContratoCnpj(formatted);
                        }}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                      />
                    </Box>

                    {/* Tipo de contrato: qual modelo do evento gera este contrato */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Tipo de contrato
                      </Text>
                      <SelectTipoContrato
                        tipos={contratoInfo.tipos_contrato ?? []}
                        value={contratoTipo}
                        onChange={setContratoTipo}
                        mostrarEstado
                        isDisabled={isReadOnly}
                        bg="white"
                      />
                      {estadoDoTipoEscolhido && (
                        <Text fontSize="10px" color="orange.600" mt={0.5}>
                          Template: {estadoDoTipoEscolhido}
                        </Text>
                      )}
                    </Box>

                    {/* Categoria */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Categoria
                      </Text>
                      <Box
                        as="select"
                        w="100%"
                        fontSize="12px"
                        px={2}
                        py={1.5}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                        _focus={{ borderColor: 'blue.400', outline: 'none' }}
                        value={contratoCategoria ?? ''}
                        onChange={(e: any) => {
                          const val = e.target.value ? Number(e.target.value) : null;
                          setContratoCategoria(val);
                          setContratoServicos([]);
                        }}
                      >
                        <option value="">Selecione...</option>
                        {(contratoInfo.categorias ?? []).map((cat: any) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nome}
                          </option>
                        ))}
                      </Box>
                    </Box>

                    {/* Servicos (multi-select como checkboxes) */}
                    {contratoCategoria && servicosFiltrados.length > 0 && (
                      <Box>
                        <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                          Servicos
                        </Text>
                        <Box
                          maxH="120px"
                          overflowY="auto"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          bg="white"
                          p={1}
                        >
                          {servicosFiltrados.map((s: any) => {
                            const checked = contratoServicos.includes(s.id);
                            return (
                              <Flex
                                key={s.id}
                                alignItems="center"
                                gap={2}
                                px={2}
                                py={1}
                                borderRadius="sm"
                                cursor="pointer"
                                bg={checked ? 'blue.50' : 'transparent'}
                                _hover={{ bg: checked ? 'blue.50' : 'gray.50' }}
                                onClick={() =>
                                  setContratoServicos(prev =>
                                    checked ? prev.filter(id => id !== s.id) : [...prev, s.id],
                                  )
                                }
                              >
                                <Box
                                  w="13px"
                                  h="13px"
                                  borderRadius="sm"
                                  border="2px solid"
                                  borderColor={checked ? 'blue.500' : 'gray.300'}
                                  bg={checked ? 'blue.500' : 'white'}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flexShrink={0}
                                >
                                  {checked && <Check size={8} color="white" weight="bold" />}
                                </Box>
                                <Text fontSize="11px" color="gray.600" noOfLines={1}>
                                  {s.nome}
                                </Text>
                              </Flex>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Valor */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Valor Orçado (R$)
                      </Text>
                      <Input
                        as={NumericFormat}
                        size="sm"
                        fontSize="12px"
                        borderRadius="md"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="R$ "
                        value={contratoValor ? parseFloat(contratoValor) : ''}
                        onValueChange={({ floatValue }: any) =>
                          setContratoValor(String(floatValue ?? ''))
                        }
                        placeholder="R$ 0,00"
                      />
                    </Box>

                    {/* Data do contrato */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Data do contrato
                      </Text>
                      <Input
                        type="date"
                        size="sm"
                        fontSize="12px"
                        borderRadius="md"
                        value={contratoDtVencimento}
                        onChange={e => setContratoDtVencimento(e.target.value)}
                      />
                    </Box>

                    {/* Botao salvar — escondido no espelho read-only */}
                    {!isReadOnly && (
                      <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={handleSaveContrato}
                        isLoading={updateContratoMutation.isPending}
                      >
                        Salvar contrato
                      </Button>
                    )}

                    {/* Dados da NF */}
                    {contratoInfo.nf_numero && (
                      <Box
                        mt={2}
                        p={2.5}
                        bg="green.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="green.100"
                      >
                        <Text
                          fontSize="10px"
                          fontWeight="600"
                          color="green.600"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Nota Fiscal
                        </Text>
                        <Text fontSize="12px" fontWeight="500" color="gray.700">
                          NF {contratoInfo.nf_numero}
                        </Text>
                        {contratoInfo.nf_valor != null && (
                          <Text fontSize="12px" color="gray.500">
                            R${' '}
                            {Number(contratoInfo.nf_valor).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                            })}
                          </Text>
                        )}
                        {/* Retenções */}
                        {[
                          { key: 'nf_retencao_iss', label: 'ISS' },
                          { key: 'nf_retencao_ir', label: 'IR' },
                          { key: 'nf_retencao_csll', label: 'CSLL' },
                          { key: 'nf_retencao_cofins', label: 'COFINS' },
                          { key: 'nf_retencao_pis', label: 'PIS' },
                          { key: 'nf_retencao_inss', label: 'INSS' },
                        ].some(
                          ({ key }) =>
                            Number(contratoInfo[key as keyof typeof contratoInfo] || 0) > 0,
                        ) && (
                          <Box mt={1.5} pt={1.5} borderTop="1px solid" borderColor="green.100">
                            <Text fontSize="10px" fontWeight="600" color="green.500" mb={1}>
                              RETENÇÕES
                            </Text>
                            {[
                              { key: 'nf_retencao_iss', label: 'ISS' },
                              { key: 'nf_retencao_ir', label: 'IR' },
                              { key: 'nf_retencao_csll', label: 'CSLL' },
                              { key: 'nf_retencao_cofins', label: 'COFINS' },
                              { key: 'nf_retencao_pis', label: 'PIS' },
                              { key: 'nf_retencao_inss', label: 'INSS' },
                            ]
                              .filter(
                                ({ key }) =>
                                  Number(contratoInfo[key as keyof typeof contratoInfo] || 0) > 0,
                              )
                              .map(({ key, label }) => (
                                <Flex key={key} justify="space-between" fontSize="11px">
                                  <Text color="gray.500">{label}</Text>
                                  <Text color="gray.600" fontWeight="500">
                                    R${' '}
                                    {Number(
                                      contratoInfo[key as keyof typeof contratoInfo],
                                    ).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                    })}
                                  </Text>
                                </Flex>
                              ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Flex>
                </Box>
              </>
            )}

            {/*
                O contrato é o TEMPLATE DO EVENTO preenchido com a ficha deste
                fornecedor. Sem o template do evento confirmado não há o que
                gerar: quem define é a faixa no topo da coluna CONTRATO.
              */}
            {showContratoIA && termoAditivo && (
              <>
                <Divider my={3} />
                <Text fontSize="11px" color="gray.600">
                  Termo aditivo: o robô de eventos preenche o modelo do jurídico e manda para
                  assinatura. O Word fica nos anexos deste card.
                </Text>
              </>
            )}
            {showContratoIA && contratoInfo && !termoAditivo && (
              <>
                <Divider my={3} />
                <Button
                  size="sm"
                  leftIcon={<FileDoc size={14} />}
                  colorScheme="blue"
                  variant="outline"
                  w="100%"
                  justifyContent="flex-start"
                  fontWeight="500"
                  fontSize="12px"
                  borderRadius="lg"
                  isDisabled={
                    tipoNaoSalvo ||
                    (!contratoInfo.template_evento?.confirmado && !contratoInfo.preenchido_em)
                  }
                  onClick={() =>
                    navigate(
                      `/eventos/contrato?evento=${contratoInfo.evento_id}` +
                        `&contrato=${contratoInfo.contrato_id}`,
                    )
                  }
                >
                  {contratoInfo.preenchido_em ? 'Rever contrato' : 'Gerar contrato'}
                </Button>
                {tipoNaoSalvo && (
                  <Text fontSize="10px" color="orange.600" mt={1}>
                    Salve o tipo de contrato antes de gerar.
                  </Text>
                )}
                {!tipoNaoSalvo && !contratoInfo.template_evento?.confirmado && (
                  <Text fontSize="10px" color="orange.600" mt={1}>
                    {contratoInfo.template_evento?.arquivos
                      ? 'Confirme o template deste tipo de contrato no topo da coluna CONTRATO.'
                      : `O evento não tem template de ${rotuloDoTipo(
                          contratoInfo.tipos_contrato,
                          contratoInfo.contrato_tipo ?? TIPO_CONTRATO_PADRAO,
                        ).toLowerCase()}: suba o modelo no topo da coluna CONTRATO ou troque o tipo do contrato.`}
                  </Text>
                )}

                {/*
                    Desfecho do preenchimento DENTRO do card. O fluxo começa
                    aqui — é deste card que se abre a tela de preenchimento —
                    então é aqui que ele tem que fechar. Sem isto o card ficava
                    com os .docx na aba Anexos sem dizer que são o contrato
                    gerado, nem se o gate liberou o envio para assinatura.
                    Só o placar: os bloqueios e o `conferir` ficam na tela de
                    preenchimento, que é onde se conserta.
                  */}
                {contratoInfo.preenchido_em && (
                  <Box
                    mt={3}
                    p={3}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor={contratoInfo.preenchimento_aprovado ? 'green.100' : 'red.100'}
                    bg={contratoInfo.preenchimento_aprovado ? 'green.50' : 'red.50'}
                  >
                    <Flex align="center" gap={2} mb={1} flexWrap="wrap">
                      <Text fontSize="11px" fontWeight="600" color="gray.700">
                        Contrato preenchido
                      </Text>
                      <Badge
                        fontSize="9px"
                        colorScheme={contratoInfo.preenchimento_aprovado ? 'green' : 'red'}
                      >
                        {contratoInfo.preenchimento_aprovado
                          ? 'sem bloqueios'
                          : `${contratoInfo.preenchimento_bloqueios} bloqueio(s)`}
                      </Badge>
                    </Flex>
                    <Text fontSize="10px" color="gray.500" mb={2}>
                      {new Date(contratoInfo.preenchido_em).toLocaleString('pt-BR')}
                      {contratoInfo.preenchimento_conferir > 0 &&
                        ` · ${contratoInfo.preenchimento_conferir} ponto(s) para conferir`}
                    </Text>
                    {(contratoInfo.preenchimento_documentos ?? []).map((d: any, i: number) => {
                      // O anexo traz a URL presignada; o contrato traz só o id.
                      const anexo = d.anexo_id ? anexos.find(a => a.id === d.anexo_id) : undefined;
                      return (
                        <Flex
                          key={d.anexo_id ?? `${d.template_nome}-${i}`}
                          align="center"
                          gap={1.5}
                          mb={1}
                        >
                          <FileDoc size={12} color="#718096" style={{ flexShrink: 0 }} />
                          {/*
                              `template_nome`, não `nome_arquivo`: o segundo repete
                              o fornecedor no fim e, truncado nesta coluna estreita,
                              os dois documentos viravam "CENA8 CENOGRAFIA-…" e
                              "CENA8 CENOGARFIA-…" — indistinguíveis, com um botão
                              de download cada. Duas linhas em vez de corte, porque
                              o que separa os dois ("NACIONAL ANEXO I" x "LOCAL")
                              está no FIM do nome, que é o que a reticência come.
                            */}
                          <Text
                            fontSize="10px"
                            color="gray.700"
                            flex={1}
                            noOfLines={2}
                            lineHeight="1.3"
                            title={d.nome_arquivo ?? d.template_nome}
                          >
                            {(d.template_nome ?? d.nome_arquivo ?? '').replace(/\.docx$/i, '')}
                          </Text>
                          {anexo && (
                            <IconButton
                              aria-label="Baixar contrato preenchido"
                              icon={<DownloadSimple size={12} />}
                              size="xs"
                              variant="ghost"
                              color="gray.500"
                              _hover={{ color: 'blue.500', bg: 'blue.50' }}
                              onClick={() => window.open(anexo.url, '_blank')}
                            />
                          )}
                        </Flex>
                      );
                    })}
                    {!contratoInfo.preenchimento_aprovado && (
                      <Text fontSize="10px" color="red.600" mt={1}>
                        Não envie para assinatura — abra o preenchimento para ver o que está
                        bloqueando.
                      </Text>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* Acoes — oculto quando o domínio já traz seu próprio arquivar (hideArchive) */}
            {isCreator && !hideArchive && (
              <>
                <Divider my={3} />
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Trash size={14} />}
                  color="red.400"
                  w="100%"
                  justifyContent="flex-start"
                  fontWeight="500"
                  fontSize="12px"
                  borderRadius="lg"
                  _hover={{ bg: 'red.50', color: 'red.500' }}
                  onClick={handleArchive}
                >
                  Arquivar card
                </Button>
              </>
            )}

            {/* Info */}
            <Divider my={3} />
            <Text fontSize="10px" color="gray.300">
              Criado em {moment(card.dt_criacao).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
